import { useMutation } from "@tanstack/react-query";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import { CLIENT_PAY_NORMALISATION_API_PATHS } from "../config";
import { RunAcceptedSchema } from "../types/clientPayNormalisationApi";
import type { ClientPayNormalisationInput } from "../types/normalisationRun";
import type { NormalisationRunResult } from "../types/normalisationRun";
import { toClientPayNormalisationError } from "../utils/clientPayNormalisationApiError";
import { mapNormalisationRunFromApi } from "../utils/mapNormalisationRunFromApi";
import { normalizeCoreApiJson } from "../utils/normalizeCoreApiJson";

/**
 * Starts a client pay-run normalisation run (Pacific.Agentic.Api #1098).
 *
 * POST multipart `file`, `importSource`, optional `payPeriodLabel`; sync
 * `normalise()` returns `201` with full run payload for first paint.
 */
export function useClientPayNormalisationRun() {
  return useMutation<NormalisationRunResult, Error, ClientPayNormalisationInput>(
    {
      mutationFn: async ({
        importSourceId,
        file,
        payPeriodLabel,
      }: ClientPayNormalisationInput) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("importSource", importSourceId);
        if (payPeriodLabel !== undefined && payPeriodLabel !== "") {
          formData.append("payPeriodLabel", payPeriodLabel);
        }

        const instance = await getCoreAxiosInstance();
        try {
          const { data } = await instance.post(
            CLIENT_PAY_NORMALISATION_API_PATHS.runs,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          );
          const accepted = RunAcceptedSchema.parse(normalizeCoreApiJson(data));
          return mapNormalisationRunFromApi(accepted.run);
        } catch (error) {
          throw toClientPayNormalisationError(error);
        }
      },
    },
  );
}
